import { Backdrop, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import { observer } from 'mobx-react-lite';
import { ReactNode, useEffect, useRef } from 'react';

import { useStore } from '@stores';
import { MOBILE_SCROLLING_DIV_ID } from '@utils';

import { BrowserTranslationWarning } from '../BrowserTranslationWarning';
import { Blacklist } from './Blacklist';
import { BlacklistHeader } from './BlacklistHeader';
import { ConfirmExit } from './ConfirmExit';
import { ModalHeader } from './ModalHeader';
import { ModalLayout } from './ModalLayout';

interface FormModalProps {
  children: ReactNode;
  hideHeaderFooter?: boolean;
}

export const FormModal = observer(
  ({ children, hideHeaderFooter = false }: FormModalProps) => {
    const {
      formStore,
      commonStore: { buttonLoading },
      decisionEngineStore: { isBlacklisted },
    } = useStore();
    const { isRampingOff } = formStore;
    const isMobile = useMediaQuery('(max-width:960px)');

    const contentStyle = {
      flexGrow: 1,
      overflowY: isMobile ? 'auto' : 'hidden',
      msOverflowStyle: 'none',
    };

    const showHeader = !hideHeaderFooter && !isBlacklisted;
    const contentRef = useRef<HTMLDivElement>(null);

    /*
      The win-back leaves the form/results screen mounted behind it, so without
      this its buttons, cards and inputs stay tabbable — a keyboard user could
      reach the results submit button through the modal. `inert` removes the
      whole subtree from focus order, hit-testing and the accessibility tree.
      Set imperatively because React 18 has no typed `inert` prop.
    */
    useEffect(() => {
      const el = contentRef.current;
      if (!el) return;
      if (isRampingOff) {
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('inert');
      }
    }, [isRampingOff]);

    return (
      <ModalLayout>
        {showHeader && <ModalHeader />}
        <Box
          ref={contentRef}
          sx={{ ...contentStyle, position: 'relative' }}
          id={MOBILE_SCROLLING_DIV_ID}
        >
          {children}
          {isBlacklisted && !isMobile && (
            <>
              <BlacklistHeader />
              <Blacklist />
            </>
          )}
          <BrowserTranslationWarning />
        </Box>
        {/*
          Desktop win-back covers the modal body rather than replacing children:
          the results screen must stay mounted so its footer keeps the primary
          CTA registered for the results-page variant to invoke. It sits outside
          the content box so the `inert` above doesn't disable the modal itself,
          and starts below the header so that close button stays usable.
        */}
        {isRampingOff && !isMobile && (
          <Box
            sx={{
              position: 'absolute',
              top: showHeader ? '3rem' : 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'common.white',
              zIndex: 1001,
            }}
          >
            <ConfirmExit />
          </Box>
        )}
        {isRampingOff && isMobile && (
          <>
            <Backdrop
              open={isRampingOff}
              // Don't let a backdrop tap dismiss the sheet mid-submit.
              onClick={() => !buttonLoading && formStore.setToggleRampingOff()}
              sx={{ zIndex: 1000 }}
            />
            <ConfirmExit />
          </>
        )}
        {isBlacklisted && isMobile && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                width: '100%',
                position: 'absolute',
              }}
            >
              <BlacklistHeader />
              <Blacklist />
            </Box>
          </>
        )}
      </ModalLayout>
    );
  }
);
